import { useAppSelector } from "../../app/hooks";
import React, { useEffect, useState } from "react";
import { convertDevicesToPixPolygons } from "../../DesignTab/devices/deviceUtils";
import { PartialMcBimSpace } from "../../utils";
import { convertMcBimSpaceToPixPolygon } from "../../DesignTab/pixpolygonSelectors";
import { DeckBimViewer } from "@multiconsult-group/multiplacer-ui";
import { IFireDeviceSquareBoxesWithCoversValidation } from "./fireDeviceValidations.utils";
import { ActionIcon, Badge, Box, Group, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import { getCombinationOfFireDeviceSquares } from "./spaceValidationsUtils";
import { DeviceCategoryEnum } from "@multiconsult-group/multiplacer-core";
import { useGetMcbimSingleMcbimSpaceQuery } from "../../../openapi";
import { selectHttpDevicesInRoom } from "../../DesignTab/devices/deviceSlice";

const SprinklerSquareWallRuleViewer: React.FC = () => {
  const [selectedCombinationIndex, setSelectedCombinationIndex] = useState<number>(0);
  const selectedSpaceNumber = useAppSelector((state) => state.design.selectedSpaceNumber);

  const projectNo = useAppSelector((state) => state.design.projectNo);

  const rotation = useAppSelector((state) => state.design.rotation);

  const { data: mcbimSpace } = useGetMcbimSingleMcbimSpaceQuery({
    name: selectedSpaceNumber as string,
    projectNo: projectNo,
  });

  const devicesInRoom = useAppSelector(selectHttpDevicesInRoom);

  const devicesAsPixPolygons = convertDevicesToPixPolygons({
    devices: devicesInRoom?.filter(
      (device) => device.deviceCategory === DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE
    ),
    typeDevices: [],
    selectedDeviceId: null,
    hideDevices: false,
    showTrueSize: false,
    isMoveable: false,
  });

  const spacePixPolygon = convertMcBimSpaceToPixPolygon({
    mcBimSpace: mcbimSpace as PartialMcBimSpace,
    dragSelectSpaces: false,
  });

  const combinationOfFireDeviceSquares = React.useMemo(() => {
    return mcbimSpace != null
      ? getCombinationOfFireDeviceSquares({
          devicesInRoom: devicesInRoom,
          mcbimSpace: mcbimSpace,
        })
      : [];
  }, [mcbimSpace, devicesInRoom]);

  useEffect(() => {
    const validIndex = combinationOfFireDeviceSquares.findIndex((comb) => comb.coverAllWalls);
    if (validIndex !== -1) {
      setSelectedCombinationIndex(validIndex);
    } else {
      setSelectedCombinationIndex(0);
    }
  }, [combinationOfFireDeviceSquares, selectedSpaceNumber]);

  const total = combinationOfFireDeviceSquares.length;
  const activeIndex = Math.min(selectedCombinationIndex, Math.max(0, total - 1));
  const activeCombination = combinationOfFireDeviceSquares[activeIndex];
  const validCount = combinationOfFireDeviceSquares.filter(
    (combination) => combination.coverAllWalls
  ).length;

  const combinationOfFireDeviceSquaresPixPolygons = React.useMemo(() => {
    return combinationOfFireDeviceSquares?.length > 0
      ? convertFireDeviceSquareBoxesToPixPolygons(activeCombination)
      : [];
  }, [combinationOfFireDeviceSquares, selectedCombinationIndex, selectedSpaceNumber]);

  const allPixPolygons = React.useMemo(() => {
    return spacePixPolygon != null
      ? [spacePixPolygon, ...devicesAsPixPolygons, ...combinationOfFireDeviceSquaresPixPolygons]
      : [];
  }, [spacePixPolygon, devicesAsPixPolygons, combinationOfFireDeviceSquaresPixPolygons]);

  return (
    <Stack h="100%" w="100%" gap="sm" style={{ minHeight: 280 }}>
      <Paper withBorder radius="md" p="sm">
        <SimpleGrid cols={2} spacing="sm" mb="sm">
          <Box>
            <Text size="xs" c="dimmed">
              Kombinasjoner testet
            </Text>
            <Text size="lg" fw={600}>
              {total}
            </Text>
          </Box>
          <Box>
            <Text size="xs" c="dimmed">
              Gyldige kombinasjoner
            </Text>
            <Text size="lg" fw={600} c={validCount > 0 ? "green.8" : "red.7"}>
              {validCount}
            </Text>
          </Box>
        </SimpleGrid>
        <Group
          justify="space-between"
          gap="xs"
          wrap="wrap"
          pt="sm"
          style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
        >
          <Group gap="xs" wrap="nowrap">
            <ActionIcon
              variant="default"
              size="md"
              radius="md"
              aria-label="Forrige kombinasjon"
              disabled={activeIndex === 0 || total === 0}
              onClick={() => setSelectedCombinationIndex(activeIndex - 1)}
            >
              <FaChevronLeft size={12} />
            </ActionIcon>
            <Text
              size="sm"
              fw={500}
              style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
            >
              {total ? `${activeIndex + 1} av ${total}` : "Ingen kombinasjoner"}
            </Text>
            <ActionIcon
              variant="default"
              size="md"
              radius="md"
              aria-label="Neste kombinasjon"
              disabled={total === 0 || activeIndex >= total - 1}
              onClick={() => setSelectedCombinationIndex(activeIndex + 1)}
            >
              <FaChevronRight size={12} />
            </ActionIcon>
          </Group>
          {activeCombination && (
            <Badge variant="light" color={activeCombination.coverAllWalls ? "green" : "red"}>
              {activeCombination.coverAllWalls ? "Alle vegger dekket" : "Ufullstendig dekning"}
            </Badge>
          )}
        </Group>
      </Paper>
      <Box
        style={{
          flex: 1,
          minHeight: 220,
          position: "relative",
          border: "1px solid var(--mantine-color-gray-3)",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <DeckBimViewer
          flip2dCoordinate={true}
          angle={rotation}
          paths={[]}
          polygons={allPixPolygons}
          zoomExtentKey={`${selectedSpaceNumber}:${activeIndex}`}
        />
      </Box>
      <Text size="xs" c="dimmed">
        Fargede felt viser dekningen fra hvert sprinklerhode.
      </Text>
    </Stack>
  );
};

export default SprinklerSquareWallRuleViewer;

const convertFireDeviceSquareBoxesToPixPolygons = (
  fireDeviceBoxesValidation: IFireDeviceSquareBoxesWithCoversValidation
): IPixPolygon[] => {
  return (fireDeviceBoxesValidation?.squares ?? []).map((square, index) => {
    return {
      id: `sprinkler-coverage-${index}`,
      points: square,
      fillColor: fireDeviceBoxesValidation.coverAllWalls ? "#69db7c" : "#ff8787",
      strokeColor: fireDeviceBoxesValidation.coverAllWalls ? "#2b8a3e" : "#c92a2a",
      strokeWidth: "2",
      text: {
        show: false,
        values: [],
        color: "#000000",
        fontSize: "12px",
      },
      isMoveable: false,
      isDragSelectable: false,
      tooltip: "",
      opacity: 0.3,
    };
  });
};
